import { Button, Form, Input, Picker, Popup, Toast } from 'react-vant';
import { FC, useEffect } from 'react';
import { queryCommunityTreeList } from '@/pages/Mine/My/service';
import { getChildrenById, getCommunities } from '@/utils/tree';
import { useSafeState } from 'ahooks';
import {
  categoryTypeEnum,
  CommunityNode,
  FloorNode,
  RegionNode,
  RoomNode,
  TreeNode,
  UnitNode,
} from '@/pages/Mine/My/config';
import { bindHouse } from '@/service/house';
import { useModel } from '@umijs/max';

interface BindHouseProps {
  visible: boolean;
  close: () => void;
}
const BindHouse: FC<BindHouseProps> = (props) => {
  const { visible, close } = props;
  const [form] = Form.useForm();
  const [communityTreeList, setCommunityTreeList] = useSafeState<TreeNode[]>(
    [],
  );
  const [communityList, setCommunityList] = useSafeState<CommunityNode[]>([]);
  const [regionList, setRegionList] = useSafeState<RegionNode[]>([]);
  const [unitList, setUnitList] = useSafeState<UnitNode[]>([]);
  const [floorList, setFloorList] = useSafeState<FloorNode[]>([]);
  const [roomList, setRoomList] = useSafeState<RoomNode[]>([]);

  const loadData = () => {
    queryCommunityTreeList().then((res) => {
      if (res?.code === 200) {
        setCommunityTreeList(res.data);
        setCommunityList(getCommunities(res.data));
      }
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const { ownerInformation, queryInformation } = useModel(
    'useDefaultRoom',
    (model) => ({
      ownerInformation: model.ownerInformation,
      queryInformation: model.queryInformation,
    }),
  );

  //小区列表
  const communityPickerData = communityList.map((item) => ({
    text: item.name,
    value: item.id,
  }));
  //片区列表
  const regionPickerData = regionList.map((item) => ({
    text: item.name,
    value: item.id,
  }));
  // 楼栋列表
  const floorPickerData = floorList.map((item) => ({
    text: item.name,
    value: item.id,
  }));

  // 单元列表
  const unitPickerData = unitList.map((item) => ({
    text: item.name,
    value: item.id,
  }));
  // 房间列表
  const roomPickerData = roomList.map((item) => ({
    text: item.name,
    value: item.id,
  }));

  const valuesChange = (changedValues: any) => {
    if (categoryTypeEnum.COMMUNITY_ID in changedValues) {
      setRegionList(
        getChildrenById(communityTreeList, changedValues.communityId),
      );
    }
    if (categoryTypeEnum.REGION_ID in changedValues) {
      setFloorList(getChildrenById(communityTreeList, changedValues.regionId));
    }
    if (categoryTypeEnum.FLOOR_ID in changedValues) {
      setUnitList(getChildrenById(communityTreeList, changedValues.floorId));
    }
    if (categoryTypeEnum.UNIT_ID in changedValues) {
      setRoomList(getChildrenById(communityTreeList, changedValues.unitId));
    }
  };

  const setValues = (
    value: string,
    data: Array<{ value: string; text: string }>,
  ): string => {
    if (value) {
      return data.find((item) => item.value === value)?.text || '';
    } else {
      return '';
    }
  };

  const onFinish = (values: any) => {
    
    const params = {
      ...values,
      communityName: setValues(values.communityId, communityPickerData),
      regionName: setValues(values.regionId, regionPickerData),
      floorName: setValues(values.floorId, floorPickerData),
      unitName: setValues(values.unitId, unitPickerData),
      roomName: setValues(values.roomId, roomPickerData),
      roomCode:
        (values.roomId &&
          roomList.find((item) => item.id === values.roomId)?.code) ||
        '',
      applyType: 'room', // 申请类型
      bindMethod: 'wechat', // 绑定类型
      ownerId: ownerInformation?.sequenceNbr,
    };

    let roomName = `${params.communityName}/${params.regionName}/${params.floorName}/${params.unitName}/${params.roomCode}`
    params.roomName = roomName; // 房屋全称

    bindHouse(params).then((res) => {
      if (res && res.code === 200) {
        Toast.success('绑定成功');
        queryInformation();
        close();
      }
    });
  };

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible]);

  return (
    <Popup
      position={'bottom'}
      style={{ height: '80%' }}
      visible={visible}
      onClose={close}
      closeable
      round
      title={'绑定'}
    >
      <Form
        form={form}
        onValuesChange={valuesChange}
        onFinish={onFinish}
        footer={
          <div style={{ margin: 16 }}>
            <Button round nativeType="submit" type="primary" block>
              提交
            </Button>
          </div>
        }
      >
        <Form.Item
          label={'选择小区'}
          isLink
          trigger={'onConfirm'}
          onClick={(_, action) => {
            action?.current?.open();
          }}
          name={'communityId'}
        >
          <Picker title={'选择小区'} columns={communityPickerData} popup>
            {(value) =>
              value
                ? communityPickerData.find((item) => item?.value === value)
                    ?.text
                : '' || '选择'
            }
          </Picker>
        </Form.Item>
        <Form.Item
          label={'选择片区'}
          isLink
          trigger={'onConfirm'}
          name={'regionId'}
          onClick={(_, action) => {
            action?.current?.open();
          }}
        >
          <Picker columns={regionPickerData} popup>
            {(value) =>
              value
                ? regionPickerData.find((item) => item.value === value)?.text
                : '' || '选择'
            }
          </Picker>
        </Form.Item>
        <Form.Item
          label={'选择楼栋'}
          isLink
          trigger={'onConfirm'}
          name={'floorId'}
          onClick={(_, action) => {
            action?.current?.open();
          }}
        >
          <Picker columns={floorPickerData} popup>
            {(value) =>
              value
                ? floorPickerData.find((item) => item.value === value)?.text
                : '' || '选择'
            }
          </Picker>
        </Form.Item>
        <Form.Item
          label={'选择单元'}
          isLink
          trigger={'onConfirm'}
          name={'unitId'}
          onClick={(_, action) => {
            action?.current?.open();
          }}
        >
          <Picker columns={unitPickerData} popup>
            {(value) =>
              value
                ? unitPickerData.find((item) => item.value === value)?.text
                : '' || '选择'
            }
          </Picker>
        </Form.Item>

        <Form.Item
          label={'选择房间'}
          isLink
          trigger={'onConfirm'}
          name={'roomId'}
          onClick={(_, action) => {
            action?.current?.open();
          }}
        >
          <Picker columns={roomPickerData} popup>
            {(value) =>
              value
                ? roomPickerData.find((item) => item.value === value)?.text
                : '' || '选择'
            }
          </Picker>
        </Form.Item>
        <Form.Item label={'业主手机'} name="ownerPhone">
          <Input type={'tel'} placeholder={'请输入业主手机'} />
        </Form.Item>
      </Form>
    </Popup>
  );
};

export default BindHouse;
